/* eslint-disable @typescript-eslint/consistent-type-imports */
import type ConversationAssistant from '@atlassian/conversation-assistant';
import type { ConversationAction } from '@atlassian/rovo-conversation-actions';
import { ConversationActionTags } from '@atlassian/rovo-conversation-actions/types';

export type RovoAction = {
	action: ConversationAction;
};

export type CustomGreetingConfig = {
	/** The icon source URL for light theme */
	iconSrc: string;
	/** The icon source URL for dark theme. Falls back to `iconSrc` if not provided. */
	iconSrcDark?: string;
	/** The greeting title text. Limited to 50 characters. */
	title: string;
};

export type RovoConfiguration = {
	getActions: React.ComponentProps<typeof ConversationAssistant>['getActions'];
	tags?: ConversationActionTags;
	nudges?: ConversationAction[];
	customGreeting?: CustomGreetingConfig;
	/**
	 * Controls whether the greeting is displayed in the empty state.
	 * When true, the greeting is always shown regardless of mode.
	 * When false, the greeting is always hidden regardless of mode.
	 * When undefined, the default behavior applies (hidden in mini modal, shown elsewhere).
	 * @default undefined
	 */
	shouldShowGreeting?: boolean;
	/**
	 * When true, only the actions fetched from the custom getActions provider are displayed,
	 * rather than being merged with the default actions.
	 * @default undefined
	 */
	prioritizeCustomActionResults?: boolean;
};

export interface RovoConfigurationProviderProps {
	setConfiguration: (config: RovoConfiguration) => void;
}

export enum RegisteredExperience {
	VIEW_PAGE = 'view-page',
	EDIT_PAGE = 'edit-page',
	WHITEBOARD = 'whiteboard',
}

export type RegisteredExperienceType = RegisteredExperience;

export type RovoProviderComponent = React.ComponentType<any>;

export type RovoConfigProviderBaseProps = {
	experience?: RegisteredExperienceType;
	provider?: RovoProviderComponent;
};

export type CommonRovoConfigProviderProps = {
	contentId: string;
	spaceKey: string;
	contentTitle: string;
};

export type PageRovoConfigProviderProps = RovoConfigProviderBaseProps &
	CommonRovoConfigProviderProps & {
		contentType: string;
		lastModifiedDate?: string | null;
		isUnpublishedDraft?: boolean;
		readTime?: number;
		readTimeLoading?: boolean;
		isEditorEmpty?: boolean;
	};

export type WhiteboardRovoConfigProviderProps = RovoConfigProviderBaseProps &
	CommonRovoConfigProviderProps & {
		actionsToRender: ConversationAction[];
	};

export type RovoConfigProviderProps =
	| PageRovoConfigProviderProps
	| WhiteboardRovoConfigProviderProps;

export const isWhiteboardRovoConfigProviderProps = (
	props: RovoConfigProviderProps,
): props is WhiteboardRovoConfigProviderProps => 'actionsToRender' in props;

export const isPageRovoConfigProviderProps = (
	props: RovoConfigProviderProps,
): props is PageRovoConfigProviderProps => 'contentType' in props;
